import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ArrowRight } from "lucide-react";
import AppLayout from "../components/AppLayout";
import MatchCard from "../components/MatchCard";
import SportIcon from "../components/SportIcon";
import ReferralStatsCard from "../components/ReferralStatsCard";
import CoachMessagesBanner from "../components/CoachMessagesBanner";
import AiRecommendations from "../components/AiRecommendations";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";

export default function Dashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [sports, setSports] = useState([]);
  const [matches, setMatches] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [filter, setFilter] = useState(null);

  useEffect(() => {
    api.get("/sports").then((r) => setSports(r.data));
    api.get("/tournaments").then((r) => setTournaments(r.data.slice(0, 3)));
  }, []);
  useEffect(() => {
    api.get(`/matches${filter ? `?sport_id=${filter}` : ""}`).then((r) => setMatches(r.data));
  }, [filter]);

  return (
    <AppLayout>
      <section className="pp-fade-up">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="overline text-xs uppercase tracking-[0.2em] text-zinc-500">{t("welcome_back")}</div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black mt-1">
              Hey {user?.first_name}, ready to <span className="pp-grad-text">play</span>?
            </h1>
            <p className="text-zinc-400 mt-2">{t("play_now_subtitle")}</p>
          </div>
          <Link to="/matches/create" data-testid="dashboard-create-match" className="pp-btn-primary px-5 py-3 rounded-xl flex items-center gap-2">
            <Plus size={18} /> {t("create_match")}
          </Link>
        </div>

        {/* Sport filter chips */}
        <div className="mt-8 flex flex-wrap gap-2" data-testid="sport-filter">
          <button data-testid="filter-all" onClick={() => setFilter(null)} className={`pp-chip ${filter === null ? "pp-chip-active" : ""}`}>All</button>
          {sports.map((s) => (
            <button key={s.id} data-testid={`filter-${s.slug}`} onClick={() => setFilter(s.id)} className={`pp-chip ${filter === s.id ? "pp-chip-active" : ""}`}>
              <SportIcon slug={s.slug} size={14} /> {s.name}
            </button>
          ))}
        </div>
      </section>

      <CoachMessagesBanner />

      <section className="mt-8">
        <AiRecommendations />
      </section>

      {/* Open matches */}
      <section className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl font-bold">{t("matches")}</h2>
          <Link to="/matches" className="text-sm text-[#00E676] hover:underline flex items-center gap-1">{t("view_all")} <ArrowRight size={14} /></Link>
        </div>
        {matches.length === 0 ? (
          <div className="pp-card p-10 text-center text-zinc-500" data-testid="dashboard-no-matches">{t("no_results")}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="dashboard-matches-grid">
            {matches.slice(0, 6).map((m) => <MatchCard key={m.id} match={m} />)}
          </div>
        )}
      </section>

      {/* Referral stats */}
      <section className="mt-10">
        <ReferralStatsCard />
      </section>

      {/* Tournaments quick view */}
      <section className="mt-12">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl font-bold">{t("tournaments")}</h2>
          <Link to="/tournaments" className="text-sm text-[#00E676] hover:underline flex items-center gap-1">{t("view_all")} <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {tournaments.map((tour) => (
            <Link key={tour.id} to={`/tournaments/${tour.id}`} data-testid={`tournament-card-${tour.id}`} className="pp-card p-5 block">
              <div className="text-xs text-[#007AFF] uppercase tracking-wider">{tour.sport?.name}</div>
              <div className="font-heading text-lg font-bold mt-1">{tour.title}</div>
              <div className="text-sm text-zinc-400 mt-2 line-clamp-2">{tour.description}</div>
              <div className="mt-4 flex justify-between text-xs text-zinc-500">
                <span>{new Date(tour.start_date).toLocaleDateString()}</span>
                <span>{tour.entry_fee > 0 ? `${tour.entry_fee} ${tour.currency}` : t("free")}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </AppLayout>
  );
}
